import { configuredCarInputToPrString } from '@oneaudi/onegraph-utils';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { DefaultsResponse } from '~/app/compositions/content/calculation-info/models/financial-disclaimer';
import { ServiceFooter } from '~/app/compositions/content/service-footer/service-footer';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import getAvpStageLink from '~/app/market-config/get-avp-stage-link';
import { FinancialProductResponse } from '~/app/market-config/models/financial-product-response';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import CarConfiPage from '~/app/pages/car-confi-page';
import { StockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { MoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { CtxConfiguratorProvider } from '~/app/pages/configurator/context/ctx-configurator';
import SummaryPage from '~/app/pages/summary/summary';
import { useInitTrackingService } from '~/app/tracking/use-init-tracking-service';
import isConsFinancingStage from '~/app/utils/is-cons-financing-stage';
import { MessageDialog } from '~/components/dialog';
import { IframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { RescueInfoDialog } from '~/components/dialog/rescue-info-dialog/rescue-info-dialog';
import { TermsAndConditionsDialog } from '~/components/dialog/terms-and-conditions-dialog/terms-and-conditions-dialog';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useConfiguredCarTrackingQuery } from '~/graphql/hooks/use-configured-car-queries';
import { state } from '~/storage';
import { Container, InnerContainer } from '~/styles/container';
import { useAppState } from '~/app/contexts/app-context';
import { ResponseNotification } from '~/app/pages/components/notification/notification';
import { saveAudicode } from '~/app/pages/summary/components/save-configuration/components/save-configuration';
import { useSaveConfigurationNotification } from '~/app/pages/components/notification/use-notification';

export default function MarketRouter() {
	const currentView = useAtomValue(state.currentView);
	const [, setDebugMode] = useAtom(state.isDebugMode);
	const carConfig = useConfigurator();
	const marketConfig = useMarketConfig();
	const marketUIConfig = useMarketUiConfig();
	const [, setDisclaimer] = useAtom(state.financingDisclaimerText);
	const preset = useAtomValue(state.fsagFinancialCalculatorSettings);
	const { financialCalculator, carIdentifier, isLoading } = useConfigurator();
	const culture = `${marketConfig.language}-${marketConfig.market.toUpperCase()}`;
	const [finProducts, setFinProducts] = useAtom(state.financialProducts);
	const [, setDefaultFinanceProduct] = useAtom(state.defaultFinancialProduct);
	const [, setAvpConfigId] = useAtom(state.AVPConfigId);
	const [, setReloadAudicode] = useAtom(state.reloadAudicode);
	const [hasConfigChanged] = useAtom(state.hasConfigChanged);
	const [hasFinancingChanged] = useAtom(state.hasFinancingChanged);
	const { i18n } = useI18n();
	useInitTrackingService();
	const { data, loading: dataIsLoading } = useConfiguredCarTrackingQuery(
		carIdentifier,
		marketConfig.config.enableFinancingServices,
	);

	const { accessToken, notificationData } = useAppState();
	const { showNotification } = useSaveConfigurationNotification();

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const debugMode = urlParams.get('debug') ?? '';
		setDebugMode(debugMode.toLowerCase() === 'true');

		urlParams.set('pr', configuredCarInputToPrString(carConfig?.carIdentifier));
		const newUrl = `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`;
		window.history.replaceState({ path: newUrl }, '', newUrl);
	}, [JSON.stringify(carConfig?.carIdentifier), setDebugMode]);

	useEffect(() => {
		if (carIdentifier.model.year) {
			setAvpConfigId(getAvpStageLink(marketConfig.config.carlineId, carIdentifier.model.year));
		}
	}, [isLoading, JSON.stringify(carConfig?.carIdentifier)]);

	useEffect(() => {
		if (hasFinancingChanged || hasConfigChanged) setReloadAudicode(true);
	}, [hasConfigChanged, hasFinancingChanged]);

	// Another Audi Service from CDN.
	// Gets the disclaimer Texts
	// TODO: can be done in backend, adjust with Team Knuckles
	useEffect(() => {
		if (!marketConfig.config.enableFinancingServices) return;
		if (dataIsLoading) return;
		if (!data?.configuredCar?.prices?.financingParameters?.externalRequestOptions?.Vehicle) return;

		if (finProducts.length > 0) return;

		setDefaultFinanceProduct(
			data?.configuredCar?.prices?.financingParameters?.externalRequestOptions.Product['@ID'],
		);
		const request = {
			Request: {
				'@Domain': data.configuredCar.prices.financingParameters.externalRequestOptions['@Domain']
					? data.configuredCar.prices.financingParameters.externalRequestOptions['@Domain']
					: marketConfig.config.financeDomain,
				'@Culture': culture,
				Vehicle: {
					...data?.configuredCar?.prices?.financingParameters?.externalRequestOptions?.Vehicle,
				},
				Product: {
					'@ID': data?.configuredCar?.prices?.financingParameters?.externalRequestOptions.Product['@ID'],
				},
			},
		};
		if (!financialCalculator) return;

		const calculator = isConsFinancingStage()
			? new financialCalculator({
					WEBCALC_API_URL: 'https://api.cons.webcalc.vwfs.io/webcalc-frontend-service',
					WEBCALC_API_STAGE: 'cons',
				})
			: new financialCalculator();

		calculator
			.getDefaultsAsync(request)
			.then((result: DefaultsResponse) => {
				const potentialDisclaimer =
					marketConfig.config.financingDisclaimer
						.map((disclaimer) => result.responses[0].parameters?.disclaimers[disclaimer])
						.join(' ') ?? '';
				if (potentialDisclaimer) setDisclaimer(potentialDisclaimer);
				else {
					const alternateDisclaimer =
						result.responses[0].result[marketConfig.config.financingDisclaimer[0]].text ?? '';
					setDisclaimer(alternateDisclaimer);
				}
			})
			.catch(() => {
				console.error('Unexpected issue fetching legal disclaimer ');
				setDisclaimer(i18n('ngc.currency.monthlyRate.footnote'));
			});

		calculator
			.getProductsAsync(request)
			.then((result: FinancialProductResponse) => {
				const { products } = result;
				const tmpProds: Array<{ key: string; val: string }> = [];
				products.forEach((item) => {
					tmpProds.push({ key: item.id, val: item.label });
				});
				if (tmpProds.length > 0) {
					setFinProducts(tmpProds);
				}
			})
			.catch(() => {
				console.error('Unexpected issue loading financial products. ');
			});
	}, [
		JSON.stringify(carIdentifier),
		JSON.stringify(marketConfig.config.carIdentifier),
		marketConfig.config.enableFinancingServices,
		marketConfig.config.financeDomain,
		JSON.stringify(marketConfig.config.financingDisclaimer),
		culture,
		JSON.stringify(data?.configuredCar?.prices?.financingParameters?.externalRequestOptions?.Vehicle),
		dataIsLoading,
		finProducts.length,
		JSON.stringify(financialCalculator),
		marketConfig.market,
		JSON.stringify(preset),
		setDisclaimer,
		setFinProducts,
		i18n,
	]);

	useEffect(() => {
		const saveConfigCode = new URLSearchParams(window.location.search).get('saveconfig');
		if (saveConfigCode) {
			const save = async () => {
				const url = new URL(window.document.location.href);
				url.searchParams.delete('saveconfig');
				window.history.pushState({ path: url.toString() }, '', url.toString());
				const result = await saveAudicode(saveConfigCode, accessToken);
				showNotification((_state) => ({ ..._state, ...result }));
			};
			save();
		}
	}, []);

	const { globalDisclaimer } = marketConfig.config;

	return (
		<>
			{globalDisclaimer && globalDisclaimer.disclaimer && globalDisclaimer.variant && (
				<Disclaimer type="global" content={globalDisclaimer.disclaimer} variant={globalDisclaimer.variant} />
			)}
			{notificationData && notificationData.copy && notificationData.title && <ResponseNotification />}
			<main id="content-area" style={{ position: 'relative' }}>
				<CtxConfiguratorProvider>
					<Container isFullSize={true}>
						<InnerContainer removeSpaces={true}>
							{currentView === 'configurator' && (
								<CarConfiPage market={marketConfig.config} marketUi={marketUIConfig} />
							)}
							{currentView === 'summary' && <SummaryPage />}
							<StockCarDialog />
						</InnerContainer>
					</Container>
					{currentView === 'configurator' && <ServiceFooter marketUi={marketUIConfig} />}
				</CtxConfiguratorProvider>
			</main>
			<RescueInfoDialog />
			<MessageDialog />
			<MoreInformationDialog />
			<TermsAndConditionsDialog />
			<IframeDialog />
		</>
	);
}
